import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { get, map, debounce, groupBy } from 'lodash';
import moment from 'moment';
import { Cheque } from '@baemingo/table-events/dist/types';

import { SideBar } from '~/Components/LayoutLeftSidebar/SideBar';

import { getOrderColor } from '../../utils';
import { searchOrders_searchOrders_results as searchOrder } from './__queries__';
import { ChequeWithTableCode } from '.';
import { StorageData } from '~Components/Storage';
import Button from '~ComponentsImproved/Button';
import { useI18n, useCurrency } from '~Hooks';
import DateInput from '~ComponentsImproved/DateInput';
import { BaseInputStyling } from '~Styles';
import Toggle from '~ComponentsImproved/Toggle';
import BackTopBar from '~ComponentsImproved/BackTopBar';

const SidebarWrapper = styled(SideBar)`
  height: 100%;
  flex: 0 0 320px;
  overflow: auto;
  position: relative;
  font-family: 'Eina01';
  font-weight: normal;
  letter-spacing: 0px;
`;

const ScrollWrapper = styled.div`
  height: calc(100% - 316px);
`;

const Scroll = styled.div`
  max-height: 100%;
  overflow-y: auto;
`;

const DateWrapper = styled.div``;

const DateInputWrapper = styled.div`
  border: 1px solid #d5d7d8;
  border-radius: 8px;
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 16px 8px;
`;

const DateRow = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: row;
  background: ${({ isActive }) => (isActive ? '#d5dfea' : '#fff')};
  padding: 4px 8px;
`;

const Icon = styled.div`
  margin-right: 4px;
  background: ${({ color }) => color};
  height: 20px;
  width: 20px;
`;

const DateRowWrapper = styled.div`
  display: flex;
  flex: 1;
`;

const Block = styled.div<{ flex2?: boolean }>`
  flex: ${({ flex2 }) => (flex2 ? 2 : 1)};
`;

const Label = styled.label`
  font-size: 16px;
  padding: 8px;
  color: #666;
  display: flex;
`;

const Filter = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px 0;
  border-bottom: 1px solid #d5d7d8;
`;

const FilterInput = styled.input`
  ${BaseInputStyling}
  padding: 4px 16px;
  border: 1px solid #d5d7d8;
`;

const Paging = styled.div`
  display: flex;
  padding: 8px;
  border-top: 1px solid #d5d7d8;
`;

const PageNumbers = styled.div`
  flex: 1;
  padding: 16px;
  text-align: center;
`;

const ToggleContainer = styled.div`
  padding: 2px;
`;

interface Props {
  loading: boolean;
  selectedCheque: Cheque | undefined;
  setSelectedCheque: (cheque: Cheque | undefined) => void;
  selectedOrderId: string | null;
  setSelectedOrderId: (id: string | null) => void;
  availableCheques: ChequeWithTableCode[];
  availableOrders: searchOrder[];
  setSearchText: (value: string | undefined) => void;
  setSearchBySum: (value: number | undefined) => void;
  chosenDate: string | null;
  setChosenDate: (value: string | null) => void;
  numberOfPages?: number;
  currentPage: number;
  nextPage: () => void;
  previousPage: () => void;
  showFilterByDevice: boolean;
  filterOrderHistoryByDevice: boolean;
  updateStorage: (data: Partial<StorageData>) => void;
}

const OrderListView = ({
  loading,
  selectedCheque,
  setSelectedCheque,
  selectedOrderId,
  setSelectedOrderId,
  availableCheques,
  availableOrders,
  setSearchText,
  setSearchBySum,
  chosenDate,
  setChosenDate,
  numberOfPages,
  currentPage,
  nextPage,
  previousPage,
  showFilterByDevice,
  filterOrderHistoryByDevice,
  updateStorage,
}: Props) => {
  const { i18n } = useI18n();

  const debouncedChangeHandler = useMemo(
    () => debounce((setState, value) => setState(value), 1000),
    [],
  );

  const currency = useCurrency();

  const orders = useMemo(() => {
    return groupBy(availableOrders, (o) =>
      moment(o.createdAt)
        .startOf('day')
        .add(5, 'hours')
        .calendar(undefined, {
          sameDay: `[${i18n('General.Today')}]`,
          nextDay: `[${i18n('General.Tomorrow')}]`,
          lastDay: `[${i18n('General.Yesterday')}]`,
          nextWeek: 'dddd',
          lastWeek: `[${i18n('General.Last')}] dddd`,
          sameElse: 'DD/MM/YYYY',
        }),
    );
  }, [availableOrders]);

  return (
    <SidebarWrapper>
      <BackTopBar title={i18n('History.Title')} />
      <Filter>
        <FilterInput
          placeholder={i18n('History.TableOrderNumber')}
          type="text"
          onChange={(e) => debouncedChangeHandler(setSearchText, e.target.value)}
        />
        <FilterInput
          placeholder={i18n('History.OrderAmount')}
          type="number"
          onChange={(e) =>
            debouncedChangeHandler(setSearchBySum, Math.round(+e.target.value * 100))
          }
        />
        <DateInputWrapper>
          <DateInput
            selectedDate={chosenDate ? new Date(chosenDate) : null}
            setSelectedDate={(date) => {
              if (date instanceof Date) {
                setChosenDate(date?.toISOString() ?? null);
              }
            }}
            maxDate={new Date()}
          />
        </DateInputWrapper>
        {showFilterByDevice && (
          <ToggleContainer>
            <Toggle
              label={i18n('History.OnlyThisDevice')}
              description={i18n('History.OnlyThisDevice.Description')}
              onToggle={() =>
                updateStorage({ filterOrderHistoryByDevice: !filterOrderHistoryByDevice })
              }
              initialValue={filterOrderHistoryByDevice}
            />
          </ToggleContainer>
        )}
      </Filter>
      <ScrollWrapper>
        <Scroll>
          {loading ? (
            <Loading>{i18n('General.LoadingDots')}</Loading>
          ) : (
            <>
              {availableCheques && availableCheques.length > 0 && (
                <DateWrapper>
                  <Label>{i18n('History.OpenCheques')}</Label>
                  {availableCheques.map((cheque) => (
                    <DateRow
                      key={cheque.id}
                      onClick={() => {
                        setSelectedOrderId(null);
                        setSelectedCheque(cheque);
                      }}
                      isActive={selectedCheque?.id === cheque.id}>
                      <Icon color="#888" />
                      <DateRowWrapper>
                        <Block flex2>{`${i18n('History.Table')} ${cheque.tableCode}`}</Block>
                        <Block>#{cheque.id.slice(0, 4).toUpperCase()}</Block>
                        <Block flex2>
                          <small
                            style={{
                              marginLeft: 10,
                            }}>
                            {moment(cheque.createdAt).format('DD/MM HH:mm')}
                          </small>
                        </Block>
                        <Block>{currency.format(cheque.paidAmount)}</Block>
                      </DateRowWrapper>
                    </DateRow>
                  ))}
                </DateWrapper>
              )}
              {(availableOrders.length > 0 || !loading) &&
                map(orders, (_orders, date) => (
                  <DateWrapper key={date}>
                    <Label>{date}</Label>
                    {_orders.map((order) => (
                      <DateRow
                        key={order.id}
                        onClick={() => {
                          setSelectedCheque(undefined);
                          setSelectedOrderId(order.id);
                        }}
                        isActive={selectedOrderId === order.id}>
                        <Icon color={getOrderColor(order.status)} />
                        <DateRowWrapper>
                          <Block>
                            {get(
                              order,
                              'user.name',
                              order.origin === 'THIRD_PARTY' ? '3RD' : order.origin,
                            )}
                          </Block>
                          <Block>
                            {order.easyId.includes('•') ? order.easyId : '#' + order.easyId}
                          </Block>
                          <Block>{moment(order.createdAt).format('HH:mm')}</Block>
                          <Block>{currency.format(order.paymentAmount + order.paymentTip)}</Block>
                        </DateRowWrapper>
                      </DateRow>
                    ))}
                  </DateWrapper>
                ))}
            </>
          )}
        </Scroll>
      </ScrollWrapper>
      {numberOfPages && (
        <Paging>
          <Button
            onTouchStart={previousPage}
            text={'<'}
            styleType="Dark"
            width="100px"
            disabled={currentPage <= 1}
          />
          <PageNumbers>
            {i18n('History.Page')} {currentPage}
          </PageNumbers>
          <Button
            onTouchStart={nextPage}
            text={'>'}
            styleType="Dark"
            width="100px"
            disabled={currentPage >= numberOfPages}
          />
        </Paging>
      )}
    </SidebarWrapper>
  );
};

export default OrderListView;
